/**
 * Formatters for common data types used throughout the application
 */

/**
 * Converts bytes to human-readable format (B, KB, MB, GB)
 * @param bytes Number of bytes to format
 * @param decimals Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "1.5 MB")
 */
export const formatBytes = (bytes: number | undefined, decimals: number = 2): string => {
  if (bytes === undefined || bytes === null || isNaN(bytes)) {
    return '0 B';
  }

  if (bytes === 0) return '0 B';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Formats a date/timestamp to human-readable format with relative time
 * Example: "August 15, 2024 at 2:30 PM (2 months ago)"
 * @param dateString ISO string or Date object
 * @returns Formatted string
 */
export const formatDateHumanReadable = (
  dateString: string | Date | undefined
): string => {
  if (!dateString) return 'Unknown';

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Format the date
    const dateOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };

    const formattedDate = date.toLocaleString('en-US', dateOptions);

    // Determine relative time
    let relativeTime = '';

    if (diffDays < 0) {
      relativeTime = 'in the future';
    } else if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        relativeTime = 'just now';
      } else if (diffHours === 1) {
        relativeTime = '1 hour ago';
      } else {
        relativeTime = `${diffHours} hours ago`;
      }
    } else if (diffDays === 1) {
      relativeTime = 'yesterday';
    } else if (diffDays < 7) {
      relativeTime = `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      relativeTime = `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      relativeTime = `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      const years = Math.floor(diffDays / 365);
      relativeTime = `${years} year${years > 1 ? 's' : ''} ago`;
    }

    return `${formattedDate} (${relativeTime})`;
  } catch (error) {
    console.warn('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Formats a date to a short format (e.g., "Aug 15, 2024")
 * @param dateString ISO string or Date object
 * @returns Formatted string
 */
export const formatDateShort = (dateString: string | Date | undefined): string => {
  if (!dateString) return 'Unknown';

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };

    return date.toLocaleString('en-US', options);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Formats a time to HH:MM:SS format
 * @param dateString ISO string or Date object
 * @returns Formatted time string
 */
export const formatTime = (dateString: string | Date | undefined): string => {
  if (!dateString) return 'Unknown';

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

    if (isNaN(date.getTime())) {
      return 'Invalid time';
    }

    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    };

    return date.toLocaleString('en-US', options);
  } catch (error) {
    console.warn('Error formatting time:', error);
    return 'Invalid time';
  }
};

/**
 * Formats a date safely for admin tables — returns '—' for empty/null/invalid dates.
 * Prevents "Invalid Date" showing in admin UI when DynamoDB items lack date fields.
 */
export const safeDate = (
  value: string | Date | null | undefined,
  opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' }
): string => {
  if (!value) return '—';
  try {
    const d = typeof value === 'string' ? new Date(value) : value;
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('es-ES', opts);
  } catch {
    return '—';
  }
};

/**
 * Formats a duration in milliseconds to human-readable format
 * Example: "2 minutes 30 seconds" or "1 hour 15 minutes"
 * @param ms Duration in milliseconds
 * @returns Formatted duration string
 */
export const formatDuration = (ms: number | undefined): string => {
  if (ms === undefined || ms === null || ms < 0) {
    return '0ms';
  }

  if (ms === 0) return '0ms';

  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));

  const parts: string[] = [];

  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);

  // If less than a second, show milliseconds
  if (parts.length === 0) {
    return `${ms}ms`;
  }

  return parts.join(' ');
};
